/**
 * A pattern is accepted only if it can be *proved* linear-time, rather than rejected when it looks
 * dangerous. Backtracking blowup needs ambiguity — two ways for the engine to match the same input —
 * and in a regex that means one of four things:
 *
 *   1. a quantified group, `(a+)+`            exponential
 *   2. quantified overlapping alternation      exponential
 *   3. a backreference                         can be worse than exponential
 *   4. adjacent open repetitions over          polynomial, O(n^k)
 *      overlapping characters, `\d*\d*x`
 *
 * The grammar below cannot express 1-3 at all. 4 is the one worth dwelling on: banning quantified
 * groups caps star height at 1, which rules out the exponential cases but leaves the polynomial one
 * — and `\d*\d*x` has no group in sight, so star-height checks (safe-regex and friends) accept it.
 * It is not the lesser problem either: measured on this codebase, `.*.*x` against 10,000 characters
 * runs for over two minutes, where the exponential `^([A-Za-z0-9]+\s?)+$` needs a 26-character
 * subject to reach 230ms.
 *
 * Rather than cap the number of repetitions — which would have to be 1 to be safe, rejecting useful
 * patterns like `^[^@]+@[^@]+$` — case 4 is closed by requiring that two open repetitions reachable
 * from one another match disjoint characters. `[^@]+@[^@]+` is unambiguous because the mandatory `@`
 * separates the two, `\d*\d*` is ambiguous because nothing does, and `\d*-?\d*` is ambiguous
 * because the thing between them can match nothing at all.
 */

const MaxPatternLength = 200;
/** A range wider than this is treated as "any character" — conservative, never permissive. */
const MaxRangeExpansion = 128;

const DigitChars = '0123456789';
const WordChars = `${DigitChars}abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ_`;
const SpaceChars = ' \t\n\r\f\v ';

/** Escapes with a defined meaning that consume exactly one character. */
const ClassEscapes: Record<string, { chars: string; negated: boolean }> = {
  d: { chars: DigitChars, negated: false },
  D: { chars: DigitChars, negated: true },
  w: { chars: WordChars, negated: false },
  W: { chars: WordChars, negated: true },
  s: { chars: SpaceChars, negated: false },
  S: { chars: SpaceChars, negated: true },
};
/** Punctuation that may be escaped to match itself. */
const LiteralEscapes = new Set('.^$\\[]*+?{}()|/-'.split(''));

type Charset = { any: true } | { any: false; chars: Set<string>; negated: boolean };

const AnyChar: Charset = { any: true };
const literalCharset = (chars: Iterable<string>, negated = false): Charset => ({
  any: false,
  chars: new Set(chars),
  negated,
});

/**
 * Whether two atoms can both match some single character. Used only to prove *non*-overlap, so
 * every uncertain case must answer "yes they overlap" and cause a rejection.
 */
const overlaps = (a: Charset, b: Charset): boolean => {
  if (a.any || b.any) return true;
  if (!a.negated && !b.negated) {
    return [...a.chars].some((char) => b.chars.has(char));
  }
  if (a.negated && b.negated) return true;
  const [negated, plain] = a.negated ? [a, b] : [b, a];
  return [...plain.chars].some((char) => !negated.chars.has(char));
};

export type PatternVerdict = { safe: true } | { safe: false; reason: string };

type Quantifier = { variable: boolean; matchesEmpty: boolean };

/**
 * `{4}` is an exact count: one way to match it, so no choice point and no ambiguity. Only a
 * quantifier whose bounds differ — `*`, `+`, `?`, `{n,}`, `{n,m}` with n !== m — creates one, so
 * only those participate in the disjointness rule.
 */
const classifyQuantifier = (quantifier: string): Quantifier => {
  if (quantifier === '*') return { variable: true, matchesEmpty: true };
  if (quantifier === '?') return { variable: true, matchesEmpty: true };
  if (quantifier === '+') return { variable: true, matchesEmpty: false };

  const [lower, upper] = quantifier.slice(1, -1).split(',');
  const matchesEmpty = lower === '0';
  if (upper === undefined) return { variable: false, matchesEmpty };
  return { variable: upper === '' || lower !== upper, matchesEmpty };
};

export const isSafePattern = (source: string): PatternVerdict => {
  if (source.length > MaxPatternLength) {
    return { safe: false, reason: `pattern must be at most ${MaxPatternLength} characters` };
  }

  let index = 0;
  const reject = (reason: string): PatternVerdict => ({ safe: false, reason });

  /**
   * Open repetitions the engine could still be sitting in when it reaches the current atom: the
   * most recent one, plus any earlier ones it could have skipped over because everything since
   * matched empty. A mandatory atom clears the list, which is what makes `[^@]+@[^@]+` safe.
   */
  let reachableRepetitions: Charset[] = [];

  const readQuantifier = (): string | null => {
    const char = source[index];
    if (char === '*' || char === '+' || char === '?') {
      index += 1;
      return char;
    }
    if (char !== '{') return null;
    const close = source.indexOf('}', index);
    if (close === -1) return null;
    const body = source.slice(index + 1, close);
    if (!/^\d+(,\d*)?$/.test(body)) return null;
    index = close + 1;
    return `{${body}}`;
  };

  /** Returns the atom's charset, or a rejection. `null` charset means the atom is zero-width. */
  const readEscape = (): PatternVerdict | { charset: Charset | null } => {
    const escaped = source[index + 1];
    if (escaped === undefined) return reject('pattern ends with a trailing backslash');
    if (/\d/.test(escaped)) return reject('backreferences are not supported');

    const known = ClassEscapes[escaped];
    if (known) {
      index += 2;
      return { charset: literalCharset(known.chars, known.negated) };
    }
    if (escaped === 'b' || escaped === 'B') {
      index += 2;
      return { charset: null };
    }
    if (!LiteralEscapes.has(escaped)) return reject(`\\${escaped} is not supported`);
    index += 2;
    return { charset: literalCharset([escaped]) };
  };

  const readCharClass = (): PatternVerdict | { charset: Charset } => {
    index += 1;
    const negated = source[index] === '^';
    if (negated) index += 1;

    const members = new Set<string>();
    let widened = false;

    while (index < source.length && source[index] !== ']') {
      let member: Charset;
      // Only a plain literal can start a range: `\d-z` is not a range in a regex, it is `\d`, `-`
      // and `z`. Tracking the character here avoids recovering it from the set afterwards.
      let literal: string | undefined;

      if (source[index] === '\\') {
        const result = readEscape();
        if ('safe' in result) return result;
        if (result.charset === null) return reject('\\b is not supported inside a character class');
        member = result.charset;
      } else {
        literal = source.charAt(index);
        member = literalCharset([literal]);
        index += 1;
      }

      const isRange =
        literal !== undefined &&
        source[index] === '-' &&
        index + 1 < source.length &&
        source[index + 1] !== ']';

      if (isRange && literal !== undefined) {
        const upper = source.charAt(index + 1);
        index += 2;
        const span = upper.charCodeAt(0) - literal.charCodeAt(0);
        if (span < 0) return reject(`range ${literal}-${upper} is out of order`);
        if (span > MaxRangeExpansion) {
          widened = true;
        } else {
          for (let code = literal.charCodeAt(0); code <= upper.charCodeAt(0); code += 1) {
            members.add(String.fromCharCode(code));
          }
        }
        continue;
      }

      if (member.any || member.negated) {
        // A negated shorthand inside a class (`[\D]`) cannot be represented as a plain member set,
        // so widen to "any" rather than under-approximate what it matches.
        widened = true;
      } else {
        for (const char of member.chars) members.add(char);
      }
    }

    if (source[index] !== ']') return reject('unterminated character class');
    if (members.size === 0 && !widened) return reject('empty character class');
    index += 1;
    return { charset: widened ? AnyChar : literalCharset(members, negated) };
  };

  /**
   * Alternation is allowed in one shape only: literal-only branches, in a group carrying no
   * quantifier of its own, as in `^(INV|REQ)-\d+$`. With no repetition inside a branch, every branch
   * matches a fixed-length span, so the group cannot be ambiguous however much the branches overlap.
   * Anything richer is case 1 or 2 above.
   *
   * The body is scanned rather than split on `|`, because `)`, `|` and the quantifier characters all
   * stand for themselves once escaped or inside a character class. `(a\)b|c)` and `(a[|]b|c)` are
   * ordinary alternations, and `[*]` is a literal asterisk rather than a repetition.
   */
  const readAltGroup = (): PatternVerdict | { charset: Charset } => {
    if (source.startsWith('(?', index)) return reject('groups with flags are not supported');

    const branches: { text: string; repeated: boolean }[] = [];
    let start = index + 1;
    let cursor = start;
    let repeated = false;
    let inClass = false;
    let close = -1;

    while (cursor < source.length) {
      const char = source[cursor];
      if (char === '\\') {
        cursor += 2;
        continue;
      }
      if (inClass) {
        if (char === ']') inClass = false;
      } else if (char === '[') {
        inClass = true;
      } else if (char === '(') {
        return reject('groups may not be nested');
      } else if (char === '*' || char === '+' || char === '?' || char === '{') {
        repeated = true;
      } else if (char === '|') {
        branches.push({ text: source.slice(start, cursor), repeated });
        start = cursor + 1;
        repeated = false;
      } else if (char === ')') {
        branches.push({ text: source.slice(start, cursor), repeated });
        close = cursor;
        break;
      }
      cursor += 1;
    }

    if (close === -1) return reject('unterminated group');
    if (branches.length < 2) return reject('groups are only supported for alternatives, as (a|b)');

    for (const { text, repeated: hasRepetition } of branches) {
      if (text === '') return reject('empty alternative in group');
      if (hasRepetition) return reject('alternatives may not contain repetition');
      const verdict = isSafePattern(text);
      if (!verdict.safe) return reject(`in alternative "${text}": ${verdict.reason}`);
    }

    index = close + 1;
    if (readQuantifier() !== null) return reject('a group of alternatives may not be repeated');
    // Fixed-length and unquantified, so it neither opens a repetition nor can match empty: it
    // separates whatever came before from whatever comes after.
    return { charset: AnyChar };
  };

  if (source[index] === '^') index += 1;

  while (index < source.length) {
    if (source[index] === '$' && index === source.length - 1) {
      index += 1;
      break;
    }

    const atomStart = index;
    const char = source.charAt(index);
    let charset: Charset | null;

    if (char === '(') {
      const result = readAltGroup();
      if ('safe' in result) return result;
      reachableRepetitions = [];
      continue;
    } else if (char === ')') {
      return reject('unmatched )');
    } else if (char === '|') {
      return reject('alternation is only supported inside a group, as (a|b)');
    } else if (char === '[') {
      const result = readCharClass();
      if ('safe' in result) return result;
      charset = result.charset;
    } else if (char === '\\') {
      const result = readEscape();
      if ('safe' in result) return result;
      charset = result.charset;
    } else if (char === '*' || char === '+' || char === '?') {
      return reject(`nothing to repeat before "${char}"`);
    } else if (char === '^' || char === '$') {
      return reject(`"${char}" is only supported as an anchor; use \\${char} to match it literally`);
    } else if (char === '.') {
      charset = AnyChar;
      index += 1;
    } else {
      charset = literalCharset([char]);
      index += 1;
    }

    if (index === atomStart) return reject(`could not parse pattern at position ${index}`);

    const raw = readQuantifier();
    if (charset === null) {
      if (raw !== null) return reject('\\b and \\B may not be repeated');
      continue;
    }

    if (raw === null) {
      reachableRepetitions = [];
      continue;
    }

    const quantifier = classifyQuantifier(raw);
    if (!quantifier.variable) {
      if (!quantifier.matchesEmpty) reachableRepetitions = [];
      continue;
    }

    const ambiguous = reachableRepetitions.find((earlier) => overlaps(earlier, charset));
    if (ambiguous) {
      return reject(
        'two open-ended repetitions may not match the same characters; separate them with a ' +
          'required character, as in [^@]+@[^@]+',
      );
    }
    reachableRepetitions = quantifier.matchesEmpty
      ? [...reachableRepetitions, charset]
      : [charset];
  }

  return { safe: true };
};
